import { ROUTE_PERMISSIONS, NAVIGATION_PERMISSIONS } from './permissions'

export interface User {
  id: string
  email: string
  name: string
  active: boolean
}

export interface Role {
  id: string
  role: string
  description?: string
  permissions?: Permission[]
}

export interface Permission {
  id: string
  name: string
  description?: string
}

export interface AvailableRole {
  id: string
  role: string
  description?: string
}

export interface AuthResponse {
  user: User
  role: Role | null
  token: string | null
  refreshToken?: string
  expiresIn: number
  availableRoles?: AvailableRole[]
  requiresRoleSelection?: boolean
}

export const auth = {
  // Get current user from localStorage
  getUser: (): User | null => {
    const userStr = localStorage.getItem('user')
    if (!userStr) return null
    try {
      return JSON.parse(userStr)
    } catch {
      return null
    }
  },

  // Get current role from localStorage
  getRole: (): Role | null => {
    const roleStr = localStorage.getItem('role')
    if (!roleStr) return null
    try {
      return JSON.parse(roleStr)
    } catch {
      return null
    }
  },

  // Get auth token from localStorage
  getToken: (): string | null => {
    return localStorage.getItem('authToken')
  },

  // Get refresh token from localStorage
  getRefreshToken: (): string | null => {
    return localStorage.getItem('refreshToken')
  },

  // Set user, role, and token in localStorage
  setAuth: (authData: AuthResponse) => {
    // Only set auth data if login is complete (not requiring role selection)
    if (!authData.requiresRoleSelection && authData.token && authData.role) {
      localStorage.setItem('user', JSON.stringify(authData.user))
      localStorage.setItem('role', JSON.stringify(authData.role))
      localStorage.setItem('authToken', authData.token)
      if (authData.refreshToken) {
        localStorage.setItem('refreshToken', authData.refreshToken)
      }
    }
  },

  // Update access and refresh tokens in localStorage (used after silent refresh)
  setTokens: (accessToken: string, refreshToken: string) => {
    localStorage.setItem('authToken', accessToken)
    localStorage.setItem('refreshToken', refreshToken)
  },

  // Clear auth data from localStorage
  clearAuth: () => {
    localStorage.removeItem('user')
    localStorage.removeItem('role')
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return !!auth.getToken() && !!auth.getUser()
  },

  // Check if user has specific permission (based on role permissions from API)
  hasPermission: (permission: string): boolean => {
    const role = auth.getRole()
    if (!role?.permissions) {
      return false
    }
    return role.permissions.some(p => p.name === permission)
  },

  // Check if user has any of the required permissions
  hasAnyPermission: (permissions: readonly string[]): boolean => {
    return permissions.some(permission => auth.hasPermission(permission))
  },

  // Check if user has all of the required permissions
  hasAllPermissions: (permissions: readonly string[]): boolean => {
    return permissions.every(permission => auth.hasPermission(permission))
  },

  // Check if user can access a specific route
  canAccessRoute: (route: string): boolean => {
    // First try exact match
    let routePermissions = ROUTE_PERMISSIONS[route as keyof typeof ROUTE_PERMISSIONS]
    
    // If no exact match, try to match parameterized routes
    if (!routePermissions) {
      // Find matching route pattern (e.g., /products/123 matches /products/:id)
      const matchingRoute = Object.keys(ROUTE_PERMISSIONS).find(pattern => {
        // Convert pattern to regex (e.g., /products/:id -> /products/\d+)
        const regexPattern = pattern
          .replace(/:[^/]+/g, '[^/]+') // Replace :param with [^/]+
          .replace(/\//g, '\\/') // Escape slashes
        const regex = new RegExp(`^${regexPattern}$`)
        return regex.test(route)
      })
      
      if (matchingRoute) {
        routePermissions = ROUTE_PERMISSIONS[matchingRoute as keyof typeof ROUTE_PERMISSIONS]
      }
    }
    
    if (!routePermissions) return true // Allow access if no permissions defined
    
    return auth.hasAnyPermission(routePermissions)
  },

  // Check if user can see a navigation item
  canSeeNavigation: (navItem: string): boolean => {
    const navPermissions = NAVIGATION_PERMISSIONS[navItem as keyof typeof NAVIGATION_PERMISSIONS]
    if (!navPermissions) {
      return true // Show if no permissions defined
    }
    
    const hasAccess = auth.hasAnyPermission(navPermissions)
    return hasAccess
  },

  // Check if user is admin
  isAdmin: (): boolean => {
    const role = auth.getRole()
    return role?.role === 'Owner' || role?.role === 'SystemAdmin'
  },
}
